import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext.jsx';
import { trackMenuOpen, trackMenuItemClick } from '../analytics.js';
import { ROUTES, isVbTippPath } from '../config/routes.js';
import LanguageSwitcher from './LanguageSwitcher.jsx';
import './HamburgerMenu.css';

/**
 * HamburgerMenu component.
 *
 * Renders a fixed hamburger toggle button that opens a slide-in drawer
 * panel from the right side with navigation links. The hamburger icon
 * animates to an X when open and acts as both open/close toggle.
 *
 * Supports:
 * - Keyboard navigation (Escape to close, focus trap within drawer)
 * - Focus restoration to toggle button on close
 * - Body scroll lock when open
 * - Analytics tracking for open and item clicks
 * - ARIA dialog with aria-modal
 */
function HamburgerMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toggleButtonRef = useRef(null);
  const drawerRef = useRef(null);
  const firstItemRef = useRef(null);

  // Match exactly `/vb-tipp` or any `/vb-tipp/*` subpath (single-sourced predicate;
  // the trailing-slash boundary keeps `/vb-tippXYZ` from suppressing the switcher).
  const isVbTippRoute = isVbTippPath(location.pathname);

  const menuItems = [
    { label: t('menu.mainPage'), path: '/' },
    { label: t('menu.blog'), path: '/blogposts' },
    { label: t('menu.webDev'), path: ROUTES.webdevService },
    { label: t('menu.vbTipp'), path: '/vb-tipp' },
    { label: t('menu.impresszum'), path: ROUTES.impresszum },
  ];

  const openMenu = useCallback(() => {
    setIsOpen(true);
    trackMenuOpen();
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    // Restore focus to the hamburger toggle button on close
    requestAnimationFrame(() => {
      if (toggleButtonRef.current) {
        toggleButtonRef.current.focus();
      }
    });
  }, []);

  // Body scroll lock + auto-focus first menu item
  useEffect(() => {
    if (isOpen) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      // Focus the first menu item when drawer opens
      requestAnimationFrame(() => {
        if (firstItemRef.current) {
          firstItemRef.current.focus();
        }
      });
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [isOpen]);

  // Escape key handler + focus trap
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeMenu();
        return;
      }

      // Focus trap: cycle Tab/Shift+Tab across the toggle button and
      // all focusable elements inside the drawer, so keyboard users can
      // reach the close control (the toggle) without knowing Escape.
      if (e.key === 'Tab' && drawerRef.current) {
        const drawerFocusable = Array.from(
          drawerRef.current.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])')
        );
        // Include the toggle button so keyboard users can Tab to the
        // close control. Falls back to drawer-only if ref is somehow null.
        if (!toggleButtonRef.current) {
          if (import.meta.env.DEV) {
            console.warn(
              'HamburgerMenu: toggleButtonRef is null during focus trap — toggle button excluded from Tab cycle.'
            );
          }
        }
        const allFocusable = toggleButtonRef.current
          ? [toggleButtonRef.current, ...drawerFocusable]
          : drawerFocusable;
        if (allFocusable.length === 0) return;

        const first = allFocusable[0];
        const last = allFocusable[allFocusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeMenu]);

  const handleItemClick = useCallback(
    (label, path) => {
      trackMenuItemClick(label);
      navigate(path);
      closeMenu();
    },
    [navigate, closeMenu]
  );

  return (
    <>
      {/* Hamburger toggle button — animates to X when open, acts as close */}
      <button
        ref={toggleButtonRef}
        className={`hamburger-menu__toggle${isOpen ? ' is-open' : ''}`}
        onClick={isOpen ? closeMenu : openMenu}
        aria-label={isOpen ? t('menu.closeMenu') : t('menu.openMenu')}
        aria-expanded={isOpen}
      >
        <span className="hamburger-menu__bar" />
        <span className="hamburger-menu__bar" />
        <span className="hamburger-menu__bar" />
      </button>

      {/* Backdrop (click-outside-to-close) */}
      <div
        className={`hamburger-menu__backdrop${isOpen ? ' is-open' : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* Slide-in drawer panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-label={t('menu.navAriaLabel')}
        aria-hidden={!isOpen}
        className={`hamburger-menu__drawer${isOpen ? ' is-open' : ''}`}
      >
        {/* Navigation items */}
        <nav className="hamburger-menu__nav" aria-label={t('menu.navAriaLabel')}>
          <ul className="hamburger-menu__list">
            {menuItems.map((item, index) => (
              <li key={item.path} className="hamburger-menu__item">
                <button
                  ref={index === 0 ? firstItemRef : undefined}
                  className="hamburger-menu__link"
                  onClick={() => handleItemClick(item.label, item.path)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Language switcher at the bottom of drawer — hidden under /vb-tipp/* (HU-only surface) */}
        {!isVbTippRoute && (
          <div className="hamburger-menu__language">
            <LanguageSwitcher variant="inline" />
          </div>
        )}
      </div>
    </>
  );
}

export default HamburgerMenu;
